import React, { SyntheticEvent, useRef } from 'react';
import styled, { css } from 'styled-components';
import { body2, body2Bold, caption1, textLinesLimitClipCss } from '@westwing/ui-kit/typography';
import { EnergyEfficiencyBadge } from '@westwing/ui-kit/ProductBadges';
import { imageLoadErrorFunction } from '@westwing/ui-kit/ProductTile/ProductTileImage';
import { useBellaSelector } from 'Client/redux/hooks';
import PriceRenderer from 'Client/components/Product/PriceRenderer';
import AddToCartBtn from 'Client/components/ProductDetailPage/AddToCartBtn';
import ThumbnailImage from 'Client/components/ProductDetailPage/Gallery/ThumbnailImage';
import { useLogger } from 'AppShell/logger/LoggerProvider';
import AddOnProductTileBadges from 'Client/components/ProductDetailPage/Overlays/AddOn/AddOnProductTileBadges';
import { ProductInterface } from 'AppShell/appshell_types';
import BellaHybridLink from 'Client/components/Generic/Links/BellaHybridLink';
import useGa from 'Client/hooks/useGa';
import AddOnProductQuantitySelector from 'Client/components/ProductDetailPage/Overlays/AddOn/AddOnProductQuantitySelector';
import { ProductList, TrackPDPActions } from 'Common/ga/types';
import normalizeGaProduct from 'Common/ga/normalize';
import useElementIntersection from 'Client/hooks/useElementIntersection';
import { safeRequestIdleCallback } from 'Common/helpers/viewport';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { ProductImageTag } from '@westwing/ui-kit/ProductTile';
import { useSegmentProductClickTracking } from '../../../../../common/segment/useSegmentProductClickTracking';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { ProductInfoBox } from '@westwing/ui-kit/Product';

const Wrapper = styled.div``;

const ProductContainer = styled(BellaHybridLink)<{ $fixed?: boolean }>`
    display: grid;
    grid-template:
        'image  .   info' ${({ $fixed }) => $fixed && ' 120px'}
        '.      .   .' 20px
        / 84px 12px 1fr;
    margin-top: 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20};
    color: ${({ theme }) => theme.colors.charcoal100};
    cursor: pointer;
`;

const ProductImage = styled.div<{ $withPadding: boolean }>`
    grid-area: image;
    width: 84px;
    height: 120px;
    display: ${({ $withPadding }) => ($withPadding ? 'flex' : 'block')};
`;

const ProductInformation = styled.div`
    grid-area: info;
    display: flex;
    flex-direction: column;
`;

const ProductTitle = styled.div`
    grid-area: title;
    ${body2Bold}
    ${textLinesLimitClipCss(1)}
`;

const ProductSize = styled.div`
    ${body2}
`;

const PriceWrapper = styled.div<{ $fixed?: boolean }>`
    padding-top: 8px;
    display: flex;
    ${({ $fixed }) =>
        !$fixed &&
        css`
            flex-wrap: wrap;
        `}
    > * {
        margin-right: 5px;
    }
`;

const ActionButtonsWrapper = styled.div`
    margin-top: 17px;
    display: flex;
    column-gap: 10px;
`;

const StyledQuantitySelector = styled(AddOnProductQuantitySelector)`
    width: 60px;
    margin-left: 0;
    pointer-events: auto;
    > label {
        height: 36px;
    }
`;

const AddToCartButton = styled(AddToCartBtn)`
    margin: 0;
    height: 36px;
    line-height: 36px;
    cursor: pointer;
`;

const StyledThumbnailImage = styled(ThumbnailImage)`
    background: none;
`;

const AddToCartButtonWrapper = styled.div`
    width: 100%;
`;

const ProductDataSheet = styled(BellaHybridLink)`
    ${caption1}
    color: ${({ theme }) => theme.colors.charcoal60};
    text-decoration: underline;
    display: block;
`;

const AddOnProductTile = ({
    product,
    position,
    ...rest
}: {
    product: ProductInterface;
    position: number;
    'data-testid': string;
}) => {
    const logger = useLogger();
    const {
        simpleSku,
        name,
        price,
        originalPrice,
        link,
        productImage,
        altTag,
        energyEfficiencyLabel,
        size,
        productDataSheetSRC,
        quantity = 0,
        isAvailable,
    } = product;
    const discountColor = useBellaSelector(state => state.configurations.specialSales.strikeThroughColor);
    const gaTracking = useGa();
    const trackSegmentProductClick = useSegmentProductClickTracking('Cross Sale Recommendation Overlay');
    const t = useBellaTranslator();

    const errorCustomData = {
        activeSimpleSku: simpleSku,
        url: link,
        imageIndex: 1,
    };
    const { p100FreistellerImages, p100EnableNewOmnibusDirective } = useFeatureToggle();

    const quantitySelectorTrackingHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        gaTracking.trackAddonOverlay({ action: TrackPDPActions.QuantitySelectorClick, label: simpleSku });
    };

    const addToCartButtonTrackingHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        gaTracking.trackAddonOverlay({ action: 'Add to Cart - Click', label: simpleSku });
    };

    const productTileClickTrackingHandler = () => {
        trackSegmentProductClick(product);
        gaTracking.trackAddonOverlay({ action: 'Recommendation - Click', label: simpleSku });
        gaTracking.trackEcProductClick(
            normalizeGaProduct(product, {
                list: ProductList.addOnProducts,
                position: position + 1,
            }),
            'CrossSale-ProductLink-Click'
        );
    };

    const impressionWrapperRef = useRef<HTMLDivElement>(null);

    useElementIntersection(impressionWrapperRef, () => {
        safeRequestIdleCallback(() => {
            gaTracking.addImpression(
                product,
                {
                    list: ProductList.addOnProducts,
                    position: position + 1,
                },
                'CrossSale-Products-Impression'
            );
        });
    });

    const showMinimumPrice = p100EnableNewOmnibusDirective && originalPrice && price !== originalPrice;
    const minimumPriceText = t('last30daysprice_short', [originalPrice]);

    return (
        <Wrapper ref={impressionWrapperRef}>
            <ProductContainer
                $fixed={!p100EnableNewOmnibusDirective}
                onClick={productTileClickTrackingHandler}
                to={link}
                data-testid={rest['data-testid']}
            >
                <AddOnProductTileBadges product={product} />
                <ProductImage $withPadding={!(p100FreistellerImages && productImage.tag === ProductImageTag.DETAIL)}>
                    <StyledThumbnailImage
                        src={productImage.path}
                        seoSrc={productImage.seoUrl}
                        imageTag={productImage.tag}
                        alt={altTag}
                        loading="lazy"
                        errorFunction={(e: SyntheticEvent<HTMLImageElement, Event>): void =>
                            imageLoadErrorFunction(e, logger, 'ADD-ON PRODUCT', errorCustomData)
                        }
                    />
                </ProductImage>
                <ProductInformation>
                    <ProductTitle>{name}</ProductTitle>
                    <ProductSize>{size}</ProductSize>
                    <PriceWrapper $fixed={!p100EnableNewOmnibusDirective}>
                        <PriceRenderer price={price} originalPrice={originalPrice} discountColor={discountColor} />
                        <EnergyEfficiencyBadge
                            testId="energy-efficiency-label"
                            label={energyEfficiencyLabel}
                            variant="small"
                        />
                        {productDataSheetSRC && (
                            <ProductDataSheet
                                onClick={e => e.stopPropagation()}
                                target="_blank"
                                to={productDataSheetSRC}
                                data-testid="energy-efficiency-data-sheet-plp"
                            >
                                {t('Product_data_sheet')}
                            </ProductDataSheet>
                        )}
                        {showMinimumPrice && <ProductInfoBox.FootNote>{minimumPriceText}</ProductInfoBox.FootNote>}
                    </PriceWrapper>
                    <ActionButtonsWrapper>
                        <div role="none" onClick={quantitySelectorTrackingHandler}>
                            <StyledQuantitySelector availableQuantity={quantity} simpleSku={simpleSku} />
                        </div>
                        <AddToCartButtonWrapper role="none" onClick={addToCartButtonTrackingHandler}>
                            <AddToCartButton
                                isAddOnOverlay
                                product={{ isAvailable, simpleSku }}
                                data-testid="add-on-add-to-cart-btn"
                            />
                        </AddToCartButtonWrapper>
                    </ActionButtonsWrapper>
                </ProductInformation>
            </ProductContainer>
        </Wrapper>
    );
};

export default AddOnProductTile;
